import { useState, useEffect } from "react";
import { useApp } from "../App";
import "../styles/B2BPage.css";
import {
    HiArrowLeft,
    HiOutlineShoppingBag,
    HiOutlineOfficeBuilding,
    HiOutlineUserGroup,
    HiOutlineTruck,
    HiOutlineColorSwatch,
    HiOutlineSparkles,
    HiOutlineDocumentText,
    HiOutlineClock,
    HiOutlineGift,
    HiOutlineUser,
    HiOutlinePhone,
    HiOutlineMail,
    HiOutlineLocationMarker,
    HiOutlineChat,
    HiOutlineCheckCircle,
    HiOutlineChartBar
} from "react-icons/hi";
import {
    FaStore,
    FaChartLine,
    FaShippingFast,
    FaRegBuilding,
    FaHandshake,
    FaRegMoneyBillAlt,
    FaRegCalendarAlt,
    FaRegStar,
    FaBullhorn,
    FaBoxes,
    FaPercentage
} from "react-icons/fa";
import { MdOutlinePriceChange, MdOutlineInventory, MdOutlineCampaign } from "react-icons/md";
import { IoIosCall, IoIosMail } from "react-icons/io";
import { useNavigate } from "react-router-dom";

const PRODUCT_OPTIONS = [
    // Lungies
    { label: "Anupama Printed Lungi", group: "Premium Printed Lungies" },
    { label: "Lancer Printed Lungi", group: "Premium Printed Lungies" },
    { label: "Mangalya Printed Lungi", group: "Premium Printed Lungies" },
    { label: "Vindhya Printed Lungi", group: "Premium Printed Lungies" },
    { label: "Deepa Checked Lungi", group: "Checked Lungies" },
    { label: "Kabaali Checked Lungi", group: "Checked Lungies" },
    { label: "Sheela Checked Lungi", group: "Checked Lungies" },
    { label: "Tendulkar Checked Lungi", group: "Checked Lungies" },
    { label: "Virat Checked Lungi", group: "Checked Lungies" },
    // Kaavi
    { label: "Kaavi Arjun", group: "Kaavi" },
    { label: "Kaavi Biju", group: "Kaavi" },
    { label: "Kaavi Mamooty", group: "Kaavi" },
    { label: "Kaavi Perthiv", group: "Kaavi" },
    { label: "Kaavi Rasi", group: "Kaavi" },
    // Bedsheets
    { label: "Bedsheets – Caliber", group: "Bedsheets" },
    { label: "Bedsheets – Day Night", group: "Bedsheets" },
    { label: "Bedsheets – Imperial", group: "Bedsheets" },
    { label: "Bedsheets – CMBS 1", group: "Chennimalai Bedsheets" },
    { label: "Bedsheets – CMBS 2", group: "Chennimalai Bedsheets" },
    // Towels
    { label: "Towel – Akash", group: "Towels" },
    { label: "Towel – Dubai", group: "Towels" },
    { label: "Towel – Gulf", group: "Towels" },
    { label: "Towel – Pepsi", group: "Towels" },
    { label: "Towel – Red Rose", group: "Towels" },
];

const TABS = [
    { key: "wholesale", label: "Wholesale Enquiry", icon: <FaStore size={20} /> },
    { key: "corporate", label: "Corporate Orders", icon: <FaRegBuilding size={20} /> },
    { key: "retailer", label: "Retailer Partnership", icon: <FaHandshake size={20} /> },
];

const EMPTY_FORM = {
    name: "", businessName: "", email: "", phone: "",
    productInterest: [], quantity: "", city: "", state: "", message: ""
};

export default function B2BPage() {
    const { showToast } = useApp();
    const [activeTab, setActiveTab] = useState("wholesale");
    const [form, setForm] = useState(EMPTY_FORM);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate()

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const type = params.get("type");
        if (type === "corporate" || type === "retailer") setActiveTab(type);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleProductToggle = (label) => {
        setForm(prev => ({
            ...prev,
            productInterest: prev.productInterest.includes(label)
                ? prev.productInterest.filter(p => p !== label)
                : [...prev.productInterest, label]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
            showToast("Please fill in all required fields.", "error"); return;
        }
        if (!form.email.includes("@")) {
            showToast("Please enter a valid email address.", "error"); return;
        }
        if (form.phone.length < 10) {
            showToast("Please enter a valid phone number.", "error"); return;
        }
        setIsSubmitting(true);
        await new Promise(r => setTimeout(r, 1000));
        showToast("Enquiry submitted! Our team will contact you within 24 hours.", "success");
        setForm(EMPTY_FORM);
        setIsSubmitting(false);
    };

    const tabContent = {
        wholesale: {
            title: "Wholesale Enquiry",
            subtitle: "Get the best market rates for bulk quantities of our premium products.",
            features: [
                { icon: <MdOutlinePriceChange size={24} />, title: "Competitive Pricing", desc: "Best wholesale rates in the market" },
                { icon: <FaShippingFast size={24} />, title: "Pan India Delivery", desc: "Safe packaging & doorstep delivery" },
                { icon: <HiOutlineColorSwatch size={24} />, title: "Customization", desc: "Color matching & design repetition" },
                { icon: <HiOutlineUserGroup size={24} />, title: "500+ Retailers", desc: "Trusted across TamilNadu & beyond" },
            ]
        },
        corporate: {
            title: "Corporate Orders",
            subtitle: "Bulk gifting, uniform procurement, and branded products for corporate clients.",
            features: [
                { icon: <HiOutlineDocumentText size={24} />, title: "GST Invoice", desc: "Proper billing for corporate accounts" },
                { icon: <HiOutlineClock size={24} />, title: "Priority Processing", desc: "Fast-tracked production & delivery" },
                { icon: <HiOutlineGift size={24} />, title: "Gift Packaging", desc: "Premium packing for gifting occasions" },
                { icon: <HiOutlineUser size={24} />, title: "Dedicated Manager", desc: "Single point of contact for all orders" },
            ]
        },
        retailer: {
            title: "Retailer Partnership",
            subtitle: "Join our growing network of retail partners and grow your business with KKB.",
            features: [
                { icon: <FaRegMoneyBillAlt size={24} />, title: "High Margins", desc: "Best-in-class retailer margins" },
                { icon: <MdOutlineInventory size={24} />, title: "Regular Supply", desc: "Consistent stock availability" },
                { icon: <FaBullhorn size={24} />, title: "Marketing Support", desc: "Display materials & brand assets" },
                { icon: <HiOutlineSparkles size={24} />, title: "Exclusive Territory", desc: "Area-based exclusivity options" },
            ]
        }
    };

    const current = tabContent[activeTab];
    const groups = [...new Set(PRODUCT_OPTIONS.map(p => p.group))];

    return (
        <div className="b2b-page">
            <div className="b2b-hero">
                <button className="b2b-back" onClick={() => navigate("/")}>
                    <HiArrowLeft size={20} /> Back
                </button>
                <h1>B2B & Bulk Orders</h1>
                <p>Partner with KKBalusamy & Co. for wholesale, corporate, and retail opportunities</p>
            </div>

            <div className="b2b-container">
                {/* Tabs */}
                <div className="b2b-tabs">
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            className={`b2b-tab ${activeTab === tab.key ? "active" : ""}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            <span className="tab-icon">{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="b2b-content">
                    {/* Info Panel */}
                    <div className="b2b-info">
                        <h2>{current.title}</h2>
                        <p className="b2b-subtitle">{current.subtitle}</p>
                        <div className="b2b-features">
                            {current.features.map(f => (
                                <div key={f.title} className="b2b-feature">
                                    <span className="feature-icon">{f.icon}</span>
                                    <div>
                                        <strong>{f.title}</strong>
                                        <span>{f.desc}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="b2b-direct-contact">
                            <h4>Direct Contact</h4>
                            <a href="tel:+919843961449">
                                <IoIosCall size={18} /> +91 98439 61449
                            </a>
                            <a href="mailto:info@kkbstore.com">
                                <IoIosMail size={18} /> info@kkbstore.com
                            </a>
                        </div>
                    </div>

                    {/* Form */}
                    <form className="b2b-form" onSubmit={handleSubmit}>
                        <h3>Submit Your Enquiry</h3>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Full Name *</label>
                                <input name="name" value={form.name} onChange={handleChange} placeholder="Your full name" required />
                            </div>
                            <div className="form-group">
                                <label>Business Name</label>
                                <input name="businessName" value={form.businessName} onChange={handleChange} placeholder="Store / company name" />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Email Address *</label>
                                <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="your@email.com" required />
                            </div>
                            <div className="form-group">
                                <label>Phone Number *</label>
                                <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="10-digit mobile number" required />
                            </div>
                        </div>

                        {/* Product Interest - grouped checkboxes */}
                        <div className="form-group full-width">
                            <label>Products of Interest</label>
                            <div className="product-groups">
                                {groups.map(group => (
                                    <div key={group} className="product-group">
                                        <span className="product-group-title">{group}</span>
                                        <div className="product-checkboxes">
                                            {PRODUCT_OPTIONS.filter(p => p.group === group).map(p => (
                                                <label key={p.label} className="product-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={form.productInterest.includes(p.label)}
                                                        onChange={() => handleProductToggle(p.label)}
                                                    />
                                                    <HiOutlineCheckCircle size={16} className="checkbox-icon" />
                                                    {p.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Approx. Quantity</label>
                                <select name="quantity" value={form.quantity} onChange={handleChange}>
                                    <option value="">Select quantity range</option>
                                    <option value="50-100 pieces">50–100 pieces</option>
                                    <option value="101-250 pieces">101–250 pieces</option>
                                    <option value="251-500 pieces">251–500 pieces</option>
                                    <option value="501-1000 pieces">501–1000 pieces</option>
                                    <option value="1000+ pieces">1000+ pieces</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>City</label>
                                <input name="city" value={form.city} onChange={handleChange} placeholder="Your city" />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>State</label>
                                <input name="state" value={form.state} onChange={handleChange} placeholder="Your state" />
                            </div>
                        </div>

                        <div className="form-group full-width">
                            <label>Message / Requirements</label>
                            <textarea name="message" value={form.message} onChange={handleChange} rows="4"
                                placeholder="Tell us about your specific requirements, preferred designs, colors, or customization needs..." />
                        </div>

                        <button type="submit" className="b2b-submit-btn" disabled={isSubmitting}>
                            {isSubmitting ? "Submitting..." : `Submit ${current.title}`}
                        </button>
                        <p className="form-note">* Our team will contact you within 24 hours with pricing and catalog details.</p>
                    </form>
                </div>
            </div>
        </div>
    );
}